// src/components/PixPayment.js
import React, { useState, useEffect } from 'react';
import { generatePixQRCodeData, simulatePixPayment } from '../services/pixService';
import { createPixPayment, updatePaymentStatus } from '../firebase/services';
import './PixPayment.css';

const PixPayment = ({ 
  value, 
  appointmentId, 
  onSuccess, 
  onCancel,
  userName 
}) => {
  const [pixData, setPixData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, paid, expired
  const [isLoading, setIsLoading] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [countdown, setCountdown] = useState(15 * 60); // 15 minutos em segundos

  // Inicializa pagamento PIX
  useEffect(() => {
    const initializePayment = async () => {
      setIsLoading(true);
      
      try {
        // Gera dados PIX
        const pixPaymentData = generatePixQRCodeData({
          value,
          description: `Agendamento ${appointmentId} - ${userName}`,
          recipient: 'BARBEARIA XPTO LTDA',
          city: 'SAO PAULO',
          pixKey: 'barbearia@email.com' // Sua chave PIX real aqui
        });
        
        setPixData(pixPaymentData);
        
        // Cria registro no Firebase
        const paymentRecord = {
          appointmentId,
          value,
          userName,
          pixCode: pixPaymentData.code,
          status: 'pending',
          expiresAt: pixPaymentData.expiration.toISOString()
        };
        
        const savedPayment = await createPixPayment(paymentRecord);
        setPaymentId(savedPayment.id);
        
      } catch (error) {
        console.error('❌ Erro ao inicializar pagamento:', error);
        alert('Erro ao gerar código PIX. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    };

    initializePayment();
  }, [value, appointmentId, userName]);

  // Contador regressivo
  useEffect(() => {
    if (countdown <= 0) {
      setPaymentStatus('expired');
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Verifica pagamento periodicamente (simulação)
  useEffect(() => {
    if (paymentStatus === 'pending' && paymentId) {
      const checkInterval = setInterval(async () => {
        try {
          // Simula verificação de pagamento
          const result = await simulatePixPayment(paymentId, value);
          
          if (result.success) {
            // Atualiza status no Firebase
            await updatePaymentStatus(paymentId, 'paid', result.txid);
            
            setPaymentStatus('paid');
            clearInterval(checkInterval);
            
            // Notifica sucesso
            if (onSuccess) onSuccess(result.txid);
          }
        } catch (error) {
          console.error('Erro ao verificar pagamento:', error);
        }
      }, 5000); // Verifica a cada 5 segundos

      return () => clearInterval(checkInterval);
    }
  }, [paymentStatus, paymentId, value, onSuccess]);

  // Formata tempo MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Copiar código PIX
  const copyToClipboard = async () => {
    if (pixData?.code) {
      try {
        await navigator.clipboard.writeText(pixData.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Erro ao copiar:', error);
        // Fallback para navegadores antigos
        const textArea = document.createElement('textarea');
        textArea.value = pixData.code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="pix-loading">
        <div className="loading-spinner"></div>
        <p>Gerando código PIX...</p>
      </div>
    );
  }

  if (paymentStatus === 'expired') {
    return (
      <div className="pix-expired">
        <h3>⏰ Código PIX Expirado</h3>
        <p>Este código PIX expirou. Por favor, gere um novo pagamento.</p>
        <button onClick={onCancel} className="btn-primary">
          Voltar
        </button>
      </div>
    );
  }

  if (paymentStatus === 'paid') {
    return (
      <div className="pix-success">
        <h3>✅ Pagamento Confirmado!</h3>
        <p>Seu pagamento foi confirmado com sucesso.</p>
        <p>Seu agendamento está confirmado.</p>
        <button onClick={() => onSuccess && onSuccess()} className="btn-primary">
          Continuar
        </button>
      </div>
    );
  }

  return (
    <div className="pix-payment-container">
      <h3>💳 Pagamento via PIX</h3>
      
      <div className="payment-info">
        <div className="info-row">
          <span className="label">Valor:</span>
          <span className="value">R$ {value.toFixed(2)}</span>
        </div>
        <div className="info-row">
          <span className="label">Válido por:</span>
          <span className={`time ${countdown < 60 ? 'warning' : ''}`}>
            {formatTime(countdown)}
          </span>
        </div>
        <div className="info-row">
          <span className="label">Status:</span>
          <span className="status pending">Aguardando pagamento</span>
        </div>
      </div>

      <div className="pix-instructions">
        <h4>Como pagar:</h4>
        <ol>
          <li>Abra seu app de banco</li>
          <li>Selecione "Pagar com PIX"</li>
          <li>Escaneie o QR Code ou cole o código</li>
          <li>Confirme o pagamento</li>
        </ol>
      </div>

      <div className="pix-code-section">
        <h4>QR Code PIX:</h4>
        <div className="qr-code-container">
          {/* Em produção, use uma biblioteca como qrcode.react */}
          <div className="qr-code-placeholder">
            <div className="qr-code-simulated">
              {/* Este seria o QR Code real */}
              <div className="qr-pattern"></div>
              <div className="qr-text">PIX</div>
            </div>
            <p className="qr-hint">Posicione a câmera aqui</p>
          </div>
          
          <div className="pix-code-text">
            <p className="code-label">Código PIX (copia e cola):</p>
            <div className="code-container">
              <code className="pix-code">
                {pixData?.code ? pixData.code.substring(0, 50) + '...' : 'Gerando código...'}
              </code>
              <button 
                onClick={copyToClipboard} 
                className="copy-btn"
                disabled={!pixData?.code}
              >
                {copied ? '✅ Copiado!' : '📋 Copiar'}
              </button>
            </div>
            <p className="code-instruction">
              Ou copie o código acima e cole no seu app de banco
            </p>
          </div>
        </div>
      </div>

      <div className="payment-actions">
        <button onClick={onCancel} className="btn-secondary">
          Cancelar
        </button>
        <button 
          onClick={() => simulatePixPayment(paymentId, value)} 
          className="btn-primary"
          disabled={paymentStatus !== 'pending'}
        >
          Já Paguei
        </button>
      </div>

      <div className="payment-note">
        <p>💡 <strong>Importante:</strong> O pagamento é processado automaticamente.</p>
        <p>Seu agendamento só será confirmado após a confirmação do pagamento.</p>
      </div>
    </div>
  );
};

export default PixPayment;